import {
  clampPointToBounds,
  DragBoundaryRect,
  getDefaultJustInTimeCardPosition,
  getJustInTimeCardViewportMaxHeight,
  getJustInTimeDragBounds,
  readMinimizedCallDockReservePx,
  ViewportSize,
} from '../justInTimeCallDragBounds.util';
import {
  BoundedDragCardSize,
  BoundedPointerDragContainerStyle,
  BoundedPointerDragHandleProps,
  useBoundedPointerDrag,
} from './useBoundedPointerDrag.hook';
import {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

/** Options for {@link useJustInTimeCallCardPosition}. */
export interface UseJustInTimeCallCardPositionOptions {
  stackIndex: number;
  isMobile: boolean;
  enabled?: boolean;
}

/** Return value for {@link useJustInTimeCallCardPosition}. */
export interface UseJustInTimeCallCardPositionResult {
  cardRootRef: RefObject<HTMLDivElement>;
  dragHandleProps: BoundedPointerDragHandleProps;
  containerPositionStyle: BoundedPointerDragContainerStyle;
  isPositioned: boolean;
  /** Max height from the card's current top down to the drag bottom bound. */
  maxHeightPx: number;
}

/**
 * Reads the current viewport size for JIT drag bounds.
 *
 * @returns Viewport width and height.
 */
function readViewportSize(): ViewportSize {
  return {
    width: window.innerWidth,
    height: window.innerHeight,
  };
}

/**
 * Positions a JIT call card within the main content area with draggable bounds.
 *
 * @param options - Stack index, mobile flag, and enablement.
 * @returns Ref, drag handle props, positioning style, and viewport max height.
 */
export function useJustInTimeCallCardPosition({
  stackIndex,
  isMobile,
  enabled = true,
}: UseJustInTimeCallCardPositionOptions): UseJustInTimeCallCardPositionResult {
  const cardRootRef = useRef<HTMLDivElement>(null);
  const hasInitializedPositionRef = useRef(false);
  const [viewportSize, setViewportSize] = useState<ViewportSize>(() =>
    typeof window === 'undefined' ? { width: 0, height: 0 } : readViewportSize()
  );
  const [dockReservePx, setDockReservePx] = useState(0);
  const [cardSize, setCardSize] = useState<BoundedDragCardSize>({
    width: 0,
    height: 0,
  });
  const [position, setPosition] = useState<{ x: number; y: number } | null>(
    null
  );

  const bounds = useMemo(
    (): DragBoundaryRect =>
      getJustInTimeDragBounds(viewportSize, {
        isMobile,
        dockReservePx,
      }),
    [dockReservePx, isMobile, viewportSize]
  );

  const initializeDefaultPosition = useCallback(
    (measuredSize: BoundedDragCardSize) => {
      if (hasInitializedPositionRef.current) {
        return;
      }

      if (measuredSize.width <= 0 || measuredSize.height <= 0) {
        return;
      }

      hasInitializedPositionRef.current = true;
      setPosition(
        getDefaultJustInTimeCardPosition({
          stackIndex,
          cardWidth: measuredSize.width,
          cardHeight: measuredSize.height,
          bounds,
        })
      );
    },
    [bounds, stackIndex]
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const updateLayoutInputs = () => {
      setViewportSize(readViewportSize());
      setDockReservePx(readMinimizedCallDockReservePx());
    };

    updateLayoutInputs();
    window.addEventListener('resize', updateLayoutInputs);

    const root = document.documentElement;
    const mutationObserver = new MutationObserver(updateLayoutInputs);
    mutationObserver.observe(root, {
      attributes: true,
      attributeFilter: ['style', 'data-minimized-call-dock'],
    });

    return () => {
      window.removeEventListener('resize', updateLayoutInputs);
      mutationObserver.disconnect();
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const element = cardRootRef.current;
    if (!element) {
      return;
    }

    const publishSize = () => {
      const nextSize = {
        width: element.offsetWidth,
        height: element.offsetHeight,
      };
      setCardSize(nextSize);
      initializeDefaultPosition(nextSize);
    };

    publishSize();

    const resizeObserver = new ResizeObserver(publishSize);
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [enabled, initializeDefaultPosition]);

  const onPositionChange = useCallback(
    (nextPosition: { x: number; y: number }) => {
      if (cardSize.width <= 0 || cardSize.height <= 0) {
        setPosition(nextPosition);
        return;
      }

      setPosition(
        clampPointToBounds(
          nextPosition.x,
          nextPosition.y,
          cardSize.width,
          cardSize.height,
          bounds
        )
      );
    },
    [bounds, cardSize.height, cardSize.width]
  );

  const { dragHandleProps, containerPositionStyle } = useBoundedPointerDrag({
    bounds,
    position,
    onPositionChange,
    cardSize,
    enabled: enabled && position != null,
  });

  return {
    cardRootRef,
    dragHandleProps,
    containerPositionStyle,
    isPositioned: position != null,
    maxHeightPx: getJustInTimeCardViewportMaxHeight(
      bounds,
      position?.y ?? bounds.top
    ),
  };
}
